import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import axios from 'axios';
import './AdminDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3006/api';

function EmpIdMapping() {
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showBulkDialog, setShowBulkDialog] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [selectedTeachers, setSelectedTeachers] = useState([]);
    const [newEmpId, setNewEmpId] = useState('');
    const [bulkEmpId, setBulkEmpId] = useState('');
    const [validationStatus, setValidationStatus] = useState({ valid: null, message: '' });
    const [bulkValidationStatus, setBulkValidationStatus] = useState({ valid: null, message: '' });
    const [showPinDialog, setShowPinDialog] = useState(false);
    const [pinInput, setPinInput] = useState('');
    const toast = React.useRef(null);

    const showToast = (severity, summary, detail) => {
        toast.current?.show({ severity, summary, detail, life: 3000 });
    };

    const fetchTeachers = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${API_BASE_URL}/admin/teachers`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTeachers(response.data.data || []);
        } catch (error) {
            console.error('Error fetching teachers:', error);
            showToast('error', 'Error', 'Failed to fetch teachers');
        } finally {
            setLoading(false);
        }
    };

    const validateEmpId = async (empId) => {
        if (!empId || empId.length < 1) {
            setValidationStatus({ valid: null, message: '' });
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${API_BASE_URL}/admin/wdms/validate-emp-id/${encodeURIComponent(empId)}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.valid) {
                setValidationStatus({ valid: true, message: response.data.message });
            } else {
                setValidationStatus({ valid: false, message: response.data.message });
            }
        } catch (error) {
            console.error('Error validating emp_id:', error);
            setValidationStatus({ valid: null, message: 'Unable to validate emp_id against WDMS' });
        }
    };

    const openEditDialog = (teacher) => {
        setSelectedTeacher(teacher);
        setNewEmpId(teacher.empId || '');
        setValidationStatus({ valid: null, message: '' });
        setShowEditDialog(true);
    };

    const handleUpdateEmpId = async () => {
        if (!selectedTeacher || !newEmpId) {
            showToast('error', 'Validation Error', 'Enrolment ID is required');
            return;
        }

        // Check WDMS validation
        if (validationStatus.valid === false) {
            showToast('error', 'Validation Error', validationStatus.message);
            return;
        }

        // Show PIN dialog
        setShowPinDialog(true);
    };

    const handlePinSubmit = async () => {
        if (pinInput !== '0218') {
            showToast('error', 'Invalid PIN', 'Incorrect PIN. emp_id change cancelled.');
            setShowPinDialog(false);
            setPinInput('');
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.put(
                `${API_BASE_URL}/admin/teachers/${selectedTeacher.id}/emp-id`,
                { empId: newEmpId, pin: pinInput },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                showToast('success', 'Success', 'Enrolment ID updated successfully');
                setShowEditDialog(false);
                setShowPinDialog(false);
                setPinInput('');
                fetchTeachers();
            } else {
                showToast('error', 'Error', response.data.message);
            }
        } catch (error) {
            console.error('Error updating emp_id:', error);
            if (error.response?.data?.message) {
                showToast('error', 'Error', error.response.data.message);
            } else {
                showToast('error', 'Error', 'Failed to update enrolment ID');
            }
        }
    };

    const handleSyncHistory = async (teacher) => {
        confirmDialog({
            message: `Trigger historical data sync for ${teacher.first_name} ${teacher.last_name}? This will sync attendance data from WDMS for the last 30 days.`,
            header: 'Confirm Sync',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    const token = localStorage.getItem('adminToken');
                    await axios.post(`${API_BASE_URL}/sync-attendance`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    showToast('success', 'Success', 'Historical data sync triggered successfully');
                } catch (error) {
                    console.error('Error triggering sync:', error);
                    showToast('error', 'Error', 'Failed to trigger historical data sync');
                }
            }
        });
    };

    const openBulkDialog = () => {
        if (selectedTeachers.length === 0) {
            showToast('warn', 'Warning', 'Please select at least one teacher');
            return;
        }
        setBulkEmpId('');
        setBulkValidationStatus({ valid: null, message: '' });
        setShowBulkDialog(true);
    };

    const handleBulkUpdate = async () => {
        if (!bulkEmpId) {
            showToast('error', 'Validation Error', 'Enrolment ID is required');
            return;
        }

        if (selectedTeachers.length === 0) {
            showToast('error', 'Validation Error', 'No teachers selected');
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');
            const updatePromises = selectedTeachers.map(teacher =>
                axios.put(`${API_BASE_URL}/admin/teachers/${teacher.id}`, {
                    empId: bulkEmpId
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            );

            await Promise.all(updatePromises);
            showToast('success', 'Success', `Updated ${selectedTeachers.length} teachers successfully`);
            setShowBulkDialog(false);
            setSelectedTeachers([]);
            fetchTeachers();
        } catch (error) {
            console.error('Error bulk updating emp_id:', error);
            showToast('error', 'Error', 'Failed to update enrolment IDs');
        }
    };

    useEffect(() => {
        fetchTeachers();
    }, []);

    const actionTemplate = (rowData) => {
        return (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button 
                    icon="pi pi-pencil" 
                    className="p-button-rounded p-button-info p-button-text" 
                    onClick={() => openEditDialog(rowData)}
                    tooltip="Edit Enrolment ID"
                />
                <Button 
                    icon="pi pi-sync" 
                    className="p-button-rounded p-button-success p-button-text" 
                    onClick={() => handleSyncHistory(rowData)}
                    tooltip="Sync Historical Data"
                />
            </div>
        );
    };

    return (
        <div className="admin-section">
            <Toast ref={toast} />
            <ConfirmDialog />
            
            <div className="section-header">
                <h2>Enrolment ID Mapping</h2>
                <p>Correct mismatches between machine emp_id and NEDP enrollement_id</p>
            </div>

            <div className="table-controls">
                <div className="search-box">
                    <i className="pi pi-search"></i>
                    <InputText 
                        value={globalFilter} 
                        onChange={(e) => setGlobalFilter(e.target.value)} 
                        placeholder="Search teachers..."
                    />
                </div>
                {selectedTeachers.length > 0 && (
                    <Button 
                        label={`Bulk Update (${selectedTeachers.length})`}
                        icon="pi pi-pencil"
                        className="p-button-warning"
                        onClick={openBulkDialog}
                        tooltip="Update selected teachers"
                    />
                )}
                <Button 
                    label="Refresh" 
                    icon="pi pi-refresh" 
                    onClick={fetchTeachers}
                    tooltip="Refresh"
                />
            </div>

            {loading ? (
                <div className="loading-overlay">
                    <i className="pi pi-spinner pi-spin"></i>
                    <p>Loading teachers...</p>
                </div>
            ) : (
                <DataTable 
                    value={teachers}
                    globalFilter={globalFilter}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    sortField="nic"
                    sortOrder={1}
                    responsiveLayout="scroll"
                    selection={selectedTeachers}
                    onSelectionChange={(e) => setSelectedTeachers(e.value)}
                    dataKey="id"
                >
                    <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
                    <Column field="nic" header="NIC" sortable style={{ minWidth: '120px' }} />
                    <Column field="first_name" header="First Name" sortable style={{ minWidth: '150px' }} />
                    <Column field="last_name" header="Last Name" sortable style={{ minWidth: '150px' }} />
                    <Column field="empId" header="Enrolment ID" sortable style={{ minWidth: '100px' }} />
                    <Column field="tid" header="TID" sortable style={{ minWidth: '80px' }} />
                    <Column body={actionTemplate} header="Actions" style={{ minWidth: '100px' }} />
                </DataTable>
            )}

            <Dialog 
                visible={showEditDialog} 
                style={{ width: '450px' }} 
                header="Update Enrolment ID"
                modal 
                onHide={() => setShowEditDialog(false)}
            >
                {selectedTeacher && (
                    <div style={{ padding: '1rem' }}>
                        <div style={{ marginBottom: '1rem' }}>
                            <label><strong>Teacher:</strong></label>
                            <p>{selectedTeacher.first_name} {selectedTeacher.last_name}</p>
                            <p><strong>NIC:</strong> {selectedTeacher.nic}</p>
                            <p><strong>Current Enrolment ID:</strong> {selectedTeacher.empId || 'Not set'}</p>
                        </div>
                        <div className="form-field">
                            <label>New Enrolment ID (emp_ID) *</label>
                            <InputText
                                value={newEmpId}
                                onChange={(e) => {
                                    setNewEmpId(e.target.value);
                                    validateEmpId(e.target.value);
                                }}
                                placeholder="Enter machine emp_id"
                                style={{ width: '100%' }}
                            />
                            {validationStatus.message && (
                                <small style={{ color: validationStatus.valid ? '#2e7d32' : '#c62828', display: 'block', marginTop: '0.5rem' }}>
                                    {validationStatus.message}
                                </small>
                            )}
                        </div>
                        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                            <Button 
                                label="Cancel" 
                                icon="pi pi-times" 
                                className="p-button-text" 
                                onClick={() => setShowEditDialog(false)} 
                            />
                            <Button 
                                label="Update" 
                                icon="pi pi-check" 
                                onClick={handleUpdateEmpId}
                                disabled={!newEmpId}
                            />
                        </div>
                    </div>
                )}
            </Dialog>

            <Dialog 
                visible={showBulkDialog} 
                style={{ width: '450px' }} 
                header="Bulk Update Enrolment ID"
                modal 
                onHide={() => setShowBulkDialog(false)}
            >
                <div style={{ padding: '1rem' }}>
                    <div style={{ marginBottom: '1rem' }}>
                        <label><strong>Selected Teachers:</strong></label>
                        <p>{selectedTeachers.length} teacher(s) selected</p>
                    </div>
                    <div className="form-field">
                        <label>New Enrolment ID (emp_ID) *</label>
                        <InputText
                            value={bulkEmpId}
                            onChange={(e) => {
                                setBulkEmpId(e.target.value);
                                validateEmpId(e.target.value);
                                setBulkValidationStatus(validationStatus);
                            }}
                            placeholder="Enter machine emp_id"
                            style={{ width: '100%' }}
                        />
                        {bulkValidationStatus.message && (
                            <small style={{ color: bulkValidationStatus.valid ? '#2e7d32' : '#c62828', display: 'block', marginTop: '0.5rem' }}>
                                {bulkValidationStatus.message}
                            </small>
                        )}
                    </div>
                    <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                        <Button 
                            label="Cancel" 
                            icon="pi pi-times" 
                            className="p-button-text" 
                            onClick={() => setShowBulkDialog(false)} 
                        />
                        <Button 
                            label="Update All" 
                            icon="pi pi-check" 
                            onClick={handleBulkUpdate}
                            disabled={!bulkEmpId}
                        />
                    </div>
                </div>
            </Dialog>

            {/* PIN Dialog for emp_id changes */}
            <Dialog
                visible={showPinDialog}
                onHide={() => {
                    setShowPinDialog(false);
                    setPinInput('');
                }}
                header="Confirm emp_id Change"
                footer={
                    <div>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => {
                                setShowPinDialog(false);
                                setPinInput('');
                            }}
                        />
                        <Button
                            label="Confirm"
                            icon="pi pi-check"
                            onClick={handlePinSubmit}
                        />
                    </div>
                }
                style={{ width: '400px' }}
            >
                <div>
                    <div style={{ marginBottom: '1rem' }}>
                        <label><strong>Teacher:</strong></label>
                        <p>{selectedTeacher?.first_name} {selectedTeacher?.last_name}</p>
                        <p><strong>New Enrolment ID:</strong> {newEmpId}</p>
                    </div>
                    <div className="form-field">
                        <label>PIN *</label>
                        <InputText
                            value={pinInput}
                            onChange={(e) => setPinInput(e.target.value)}
                            type="password"
                            placeholder="Enter PIN"
                            style={{ width: '100%', fontSize: '1.2rem', fontWeight: 'bold', textAlign: 'center', letterSpacing: '4px' }}
                            maxLength={4}
                            autoFocus
                        />
                    </div>
                    {validationStatus.message && (
                        <small style={{ 
                            color: validationStatus.valid ? '#2e7d32' : '#c62828',
                            display: 'block',
                            marginTop: '8px'
                        }}>
                            {validationStatus.message}
                        </small>
                    )}
                </div>
            </Dialog>
        </div>
    );
}

export default EmpIdMapping;
